<template>
    <div class="relative flex flex-col gap-4">
        <div class="flex justify-between">
            <span>{{ $t('match_settings.ring_custom') }}</span>
            <Toggle v-model="customMode" />
        </div>
        <div class="flex flex-col gap-2">
            <span>{{ $t('match_settings.which_segment_question') }}</span>
            <div
                class="flex items-center justify-between gap-10 border border-zinc-700 bg-zinc-900 p-4"
                v-if="customMode"
                @click="selectionDrawerVisible = true"
            >
            <span class=" line-clamp-1 text-ellipsis">
                {{ selectedSummaryLabel }}
                </span>
                <svg class="h-5" viewBox="0 0 9 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M-1.23621e-07 12L5.29297 6.70703L-1.04907e-06 1.41406L1.41406 5.86347e-07L8.12109 6.70703L1.41406 13.4141L-1.23621e-07 12Z"
                        fill="white"
                    />
                </svg>
            </div>
            <CustomSelect
                v-else
                v-model="mode"
                :options="modeOptions"
                :placeholder="$t('match_settings.select_segments_placeholder')"
            />
        </div>
    </div>
    <DrawerRoot v-model:open="selectionDrawerVisible">
        <DrawerPortal>
            <DrawerOverlay class="fixed inset-0 z-[30] bg-black/80" />
            <DrawerContent
                aria-describedby="undefined"
                class="bg-bg border-stroke-1 fixed inset-x-0 bottom-0 z-[31] flex h-[90vh] flex-col rounded-t-lg border-t border-r border-l"
            >
                <DrawerHandle class="mx-auto mt-2 mb-2 h-1 w-10 rounded-full bg-zinc-600" />
                <div class="flex min-h-0 flex-1 flex-col gap-4 p-4">
                    <span
                        ><span class=" text-titles mr-2">{{ $t('match_settings.segments_selected_header') }}</span> <span>{{ activeTabCount }}/20</span></span
                    >
                    <TabsRoot v-model="activeTab" class="flex min-h-0 flex-1 flex-col">
                        <TabsList class="relative flex gap-4">
                            <TabsIndicator
                                class="absolute bottom-0 left-0 z-10 h-0.5 w-(--reka-tabs-indicator-size) translate-x-(--reka-tabs-indicator-position) rounded-full bg-red-500 transition-[width,transform] duration-200"
                            />
                            <TabsTrigger
                                v-for="tab in tabs"
                                :key="tab.value"
                                :value="tab.value"
                                class="flex-1 border-b-2 border-zinc-700 py-3 text-sm font-bold font-title uppercase text-titles transition-[color,filter] data-[state=active]:text-red-500 data-[state=active]:drop-shadow-[0_0_12px_rgba(239,68,68,1)]"
                            >
                                {{ $t(tab.labelKey) }}
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent
                            v-for="tab in tabs"
                            :key="tab.value"
                            :value="tab.value"
                            class="min-h-0 flex-1 overflow-y-auto py-4 focus:outline-none"
                        >
                            <div class="grid grid-cols-2 gap-2">
                                <button
                                    v-for="segment in segmentsForTab(tab.value)"
                                    :key="segment.type + segment.number"
                                    type="button"
                                    class="flex items-center border p-3 gap-3 font-bold transition-colors border-zinc-700 bg-card disabled:opacity-40"
                                    :disabled="!isSelected(segment) && isTypeFull(segment.type)"
                                    @click="toggleSegment(segment)"
                                >
                                    <div class=" w-4 h-4 border-zinc-700 flex items-center justify-center" :class="isSelected(segment) ? 'bg-red-500' : 'border bg-zinc-900'">
                                        <svg class="w-2.5" viewBox="0 0 9 7" fill="none" xmlns="http://www.w3.org/2000/svg" v-if="isSelected(segment)">
                                        <path d="M8.94271 0.942708L3.13802 6.7474L0 3.60938L0.942708 2.66667L3.13802 4.86198L8 0L8.94271 0.942708Z" fill="white"/>
                                        </svg>
                                    </div>
                                    {{ segment.label }}
                                </button>
                            </div>
                        </TabsContent>
                    </TabsRoot>
                </div>
                <div class="w-full flex px-4">

                    <DiagonalCutButon
                    class=" mb-4 mt-auto w-full"
                    @click="selectionDrawerVisible = false"
                    >
                    {{  $t('match_settings.confirm') }}
                </DiagonalCutButon>
            </div>
            </DrawerContent>
        </DrawerPortal>
    </DrawerRoot>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import Toggle from '@/components/ui/Form/Toggle.vue'
import CustomSelect from '@/components/ui/Form/Select.vue'
import { DrawerRoot, DrawerPortal, DrawerOverlay, DrawerContent, DrawerHandle } from 'vaul-vue'
import { TabsContent, TabsIndicator, TabsList, TabsRoot, TabsTrigger } from 'reka-ui'
import { DART_TYPES } from '@/constants/dartTypes.js'
import DiagonalCutButon from '@/components/ui/DiagonalCutButton.vue'

export type SegmentType = 'S' | 'D' | 'T' | 'B' | 'B.E'

export interface Target {
    number: number
    type: SegmentType
}

type TabValue = 's' | 'd' | 't'

interface TabDef {
    value: TabValue
    /** i18n key, not literal text — resolve with $t() at render time. */
    labelKey: string
    type: SegmentType
}


const SINGLE = DART_TYPES.SINGLE as SegmentType
const DOUBLE = DART_TYPES.DOUBLE as SegmentType
const TRIPLE = DART_TYPES.TRIPLE as SegmentType
const OUTER_BULL = DART_TYPES.OUTER_BULL as SegmentType
const BULL = DART_TYPES.BULL as SegmentType

const MAX_TARGETS = 20

const TABS: TabDef[] = [
    { value: 's', labelKey: 'match_settings.ring_singles', type: SINGLE },
    { value: 'd', labelKey: 'match_settings.ring_doubles', type: DOUBLE },
    { value: 't', labelKey: 'match_settings.ring_triples', type: TRIPLE },
]

export default defineComponent({
    name: 'SegmentsSelect',
    props: {
        modelValue: {
            type: Array as PropType<Target[]>,
            default: () => [],
        },
        options: {
            type: Array as PropType<unknown[]>,
            required: false,
            default: () => [],
        },
    },
    emits: ['update:modelValue', 'ring-mode-changed'],
    components: {
        Toggle,
        CustomSelect,
        DrawerRoot,
        DrawerPortal,
        DrawerOverlay,
        DrawerContent,
        DrawerHandle,
        TabsRoot,
        TabsList,
        TabsIndicator,
        TabsTrigger,
        TabsContent,
        DiagonalCutButon,
    },
    data() {
        // Restore the editing UI from a previously-saved v-model. Bulls are a
        // separate setting, so drop them; a full single-type ring maps back to ring
        // mode, anything else is a custom selection. Computed here (not in a
        // lifecycle hook) so hydration never trips the customMode watcher's reset.
        const picks = this.modelValue
            .filter((target) => target.type !== OUTER_BULL && target.type !== BULL)
            .map((target) => ({ number: target.number, type: target.type }))
        const ringTab = this.ringTabForTargets(picks)

        return {
            /** The non-custom ring: 's' | 'd' | 't'. */
            mode: (ringTab ?? 's') as TabValue,
            customMode: ringTab === null && picks.length > 0,
            selectionDrawerVisible: false,
            activeTab: (ringTab ?? 's') as TabValue,
            tabs: TABS,
            /** Explicitly picked targets (custom mode); bulls are excluded here. */
            selectedSegments: (ringTab === null ? picks : []) as Target[],
        }
    },
    computed: {
        /** Selected count for the active tab's ring — the X in the X/20 counter. */
        activeTabCount(): number {
            const type = TABS.find((tab) => tab.value === this.activeTab)?.type ?? SINGLE
            return this.countForType(type)
        },
        /** Translated options for the ring-mode select (Single/Double/Triple). */
        modeOptions() {
            return [
                { value: 's', label: this.$t('match_settings.dart_type_single') },
                { value: 'd', label: this.$t('match_settings.dart_type_double') },
                { value: 't', label: this.$t('match_settings.dart_type_triple') },
            ]
        },
        /** Translated summary shown on the custom-selection trigger. */
        selectedSummaryLabel(): string {
            if (!this.selectedSegments.length) {
                return this.$t('match_settings.no_targets_selected')
            }
            const list = this.selectedSegments
                .map((segment) => segment.type + segment.number)
                .join(', ')
            return this.$t('match_settings.segments_selected_list', { list })
        },
    },
    watch: {
        // Toggling custom mode resets the manual picks: entering custom starts from an
        // empty list (no need to uncheck the previous ring), and leaving custom falls
        // back to "all of the selected ring" via emitTargets().
        customMode() {
            this.selectedSegments = []
            this.emitTargets()
            this.emitRingMode()
        },
        mode() {
            if (!this.customMode) {
                this.emitTargets()
                this.emitRingMode()
            }
        },
    },
    mounted() {
        // Re-emit the hydrated (or default) selection so the parent's v-model is
        // the resolved target-array shape. Idempotent: a saved selection emits
        // itself unchanged; an empty one normalizes to the default ring.
        this.emitTargets()
        // Also report the initial ring, so a fresh match (already defaulted to a
        // single/double ring) gets its bull default pre-populated on load, same
        // as when the user picks a ring by hand.
        this.emitRingMode(true)
    },
    methods: {
        /**
         * If `targets` are exactly the 20 numbers 1-20 of a single ring (S/D/T),
         * return that ring's tab value; otherwise null (an empty, partial, or
         * mixed selection — i.e. a custom pick). Used to restore the UI mode from
         * a saved v-model.
         */
        ringTabForTargets(targets: Target[]): TabValue | null {
            if (targets.length !== MAX_TARGETS) {
                return null
            }
            const tab = TABS.find((candidate) => candidate.type === targets[0].type)
            if (!tab) {
                return null
            }
            const numbers = new Set(targets.map((target) => target.number))
            const isFullRing =
                numbers.size === MAX_TARGETS &&
                targets.every((target) => target.type === tab.type)
            return isFullRing ? tab.value : null
        },
        /**
         * Build the selectable targets for a tab: numbers 1-20 for every ring.
         * Bulls are a separate setting and folded in at submit time, so they are
         * not listed here.
         */
        segmentsForTab(tabValue: TabValue): (Target & { label: string })[] {
            const type = TABS.find((tab) => tab.value === tabValue)?.type ?? SINGLE
            const segments: (Target & { label: string })[] = []
            for (let number = 1; number <= 20; number++) {
                segments.push({ number, type, label: number.toString() })
            }
            return segments
        },
        /** Number of currently-selected targets of a given ring type. */
        countForType(type: SegmentType): number {
            return this.selectedSegments.filter((target) => target.type === type).length
        },
        /** True once a ring type has reached the per-table max (20). */
        isTypeFull(type: SegmentType): boolean {
            return this.countForType(type) >= MAX_TARGETS
        },
        isSelected(segment: Target): boolean {
            return this.selectedSegments.some(
                (target) => target.type === segment.type && target.number === segment.number,
            )
        },
        toggleSegment(segment: Target): void {
            const index = this.selectedSegments.findIndex(
                (target) => target.type === segment.type && target.number === segment.number,
            )
            if (index === -1) {
                if (this.isTypeFull(segment.type)) {
                    return
                }
                this.selectedSegments.push({ number: segment.number, type: segment.type })
            } else {
                this.selectedSegments.splice(index, 1)
            }
            this.emitTargets()
        },
        /**
         * Resolve the active selection to the final target array and emit it.
         * Custom mode emits the picked targets; otherwise all 20 of the chosen ring.
         * Bulls are handled separately (their own setting) and folded in at submit
         * time, so this component never emits them.
         */
        emitTargets(): void {
            let targets: Target[]
            if (this.customMode) {
                targets = this.selectedSegments.map((target) => ({
                    number: target.number,
                    type: target.type,
                }))
            } else {
                const type = TABS.find((tab) => tab.value === this.mode)?.type ?? SINGLE
                targets = Array.from({ length: 20 }, (_, index) => ({ number: index + 1, type }))
            }
            this.$emit('update:modelValue', targets)
        },
        /**
         * Notify the parent which ring is selected, so it can seed the matching
         * bull default (single ring -> single bull, double ring -> bullseye).
         * Not emitted in custom mode, since a custom pick has no single ring to
         * derive a bull default from. `isInitial` marks the mount-time call so
         * the parent can avoid clobbering a restored match's saved bull_targets.
         */
        emitRingMode(isInitial = false): void {
            if (this.customMode) {
                return
            }
            this.$emit('ring-mode-changed', this.mode, { isInitial })
        },
    },
})
</script>
